import { getStoreInfo } from './api';

export type TaxCalculationMode = 'api' | 'item' | 'global' | 'custom';
export type TaxDisplayMode = 'detailed' | 'simple' | 'hidden';
export type TaxIncludedMode = 'auto' | 'included' | 'excluded';

export interface TicketTaxSettings {
  calculationMode: TaxCalculationMode;
  displayMode: TaxDisplayMode;
  customRate: number;
  showItemTaxDetail: boolean;
  showCategories: boolean;
  taxIncludedMode: TaxIncludedMode;
}

export interface ItemTaxDetail {
  name: string;
  quantity: number;
  unitPrice: number;
  priceHT: number;
  taxRate: number;
  taxAmount: number;
  priceTTC: number;
}

export interface TicketTaxResult {
  showTax: boolean;
  isTaxIncluded: boolean;
  subtotalHT: number;
  taxAmount: number;
  taxRate: number | null;
  totalTTC: number;
  itemDetails: ItemTaxDetail[];
}

const STORAGE_KEY = 'ticketTaxSettings';

const DEFAULT_SETTINGS: TicketTaxSettings = {
  calculationMode: 'api',
  displayMode: 'detailed',
  customRate: 20,
  showItemTaxDetail: false,
  showCategories: true,
  taxIncludedMode: 'included',
};

export const getTicketTaxSettings = (): TicketTaxSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
  }
  return DEFAULT_SETTINGS;
};

export const saveTicketTaxSettings = (settings: TicketTaxSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
  }
};

interface CartItemForTax {
  menuItem: {
    name: string;
    price: number;
    tax_details?: Array<{ tax: number }>;
  };
  quantity: number;
}

export const calculateTicketTax = (
  items: CartItemForTax[],
  invoiceData?: { subtotal?: number; displayTotal?: number }
): TicketTaxResult => {
  const storeInfo = getStoreInfo();
  const settings = getTicketTaxSettings();

  let isTaxIncluded: boolean;
  if (settings.taxIncludedMode === 'auto') {
    isTaxIncluded = storeInfo?.is_tax_included ?? true;
  } else {
    isTaxIncluded = settings.taxIncludedMode === 'included';
  }

  const isUseItemTax = storeInfo?.is_use_item_tax ?? false;
  const apiTaxRate = storeInfo?.item_tax ?? 0;
  const fallbackTaxRate = settings.customRate > 0 ? settings.customRate : 20;

  const itemDetails: ItemTaxDetail[] = [];
  let subtotalHT = 0;
  let totalTaxAmount = 0;
  let totalTTC = 0;

  let effectiveTaxRate: number | null = null;

  if (settings.calculationMode === 'custom') {
    effectiveTaxRate = settings.customRate;
  } else if (settings.calculationMode === 'global') {
    effectiveTaxRate = apiTaxRate > 0 ? apiTaxRate : fallbackTaxRate;
  } else if (settings.calculationMode === 'api') {
    effectiveTaxRate = apiTaxRate;
  }

  for (const item of items) {
    const unitPrice = item.menuItem.price;
    const quantity = item.quantity;
    const totalItemPrice = unitPrice * quantity;

    let itemTaxRate: number;

    if (settings.calculationMode === 'api') {
      if (isUseItemTax && item.menuItem.tax_details && item.menuItem.tax_details.length > 0) {
        itemTaxRate = item.menuItem.tax_details.reduce((sum, t) => sum + (t.tax || 0), 0);
      } else {
        itemTaxRate = apiTaxRate;
      }
    } else if (settings.calculationMode === 'item') {
      if (item.menuItem.tax_details && item.menuItem.tax_details.length > 0) {
        const apiRate = item.menuItem.tax_details.reduce((sum, t) => sum + (t.tax || 0), 0);
        itemTaxRate = apiRate > 0 ? apiRate : fallbackTaxRate;
      } else {
        itemTaxRate = fallbackTaxRate;
      }
    } else if (settings.calculationMode === 'global') {
      itemTaxRate = apiTaxRate > 0 ? apiTaxRate : fallbackTaxRate;
    } else {
      itemTaxRate = settings.customRate;
    }

    const taxRateDecimal = itemTaxRate / 100;

    let priceHT: number;
    let taxAmount: number;
    let priceTTC: number;

    if (isTaxIncluded) {
      priceTTC = totalItemPrice;
      priceHT = taxRateDecimal > 0 ? totalItemPrice / (1 + taxRateDecimal) : totalItemPrice;
      taxAmount = priceTTC - priceHT;
    } else {
      priceHT = totalItemPrice;
      taxAmount = priceHT * taxRateDecimal;
      priceTTC = priceHT + taxAmount;
    }

    itemDetails.push({
      name: item.menuItem.name,
      quantity,
      unitPrice,
      priceHT,
      taxRate: itemTaxRate,
      taxAmount,
      priceTTC,
    });

    subtotalHT += priceHT;
    totalTaxAmount += taxAmount;
    totalTTC += priceTTC;
  }

  if (settings.calculationMode === 'api' && invoiceData?.subtotal !== undefined && invoiceData?.displayTotal !== undefined) {
    subtotalHT = invoiceData.subtotal;
    totalTTC = invoiceData.displayTotal;
    totalTaxAmount = totalTTC - subtotalHT;
  } else if ((settings.calculationMode === 'custom' || settings.calculationMode === 'global') && invoiceData?.displayTotal !== undefined) {
    const baseTotal = invoiceData.displayTotal;
    const rate = settings.calculationMode === 'custom' ? settings.customRate : (apiTaxRate > 0 ? apiTaxRate : fallbackTaxRate);
    const rateDecimal = rate / 100;

    if (isTaxIncluded) {
      totalTTC = baseTotal;
      subtotalHT = rateDecimal > 0 ? baseTotal / (1 + rateDecimal) : baseTotal;
      totalTaxAmount = totalTTC - subtotalHT;
    } else {
      subtotalHT = baseTotal;
      totalTaxAmount = subtotalHT * rateDecimal;
      totalTTC = subtotalHT + totalTaxAmount;
    }
  }

  if (effectiveTaxRate === null) {
    const uniqueRates = [...new Set(itemDetails.map(item => item.taxRate).filter(r => r > 0))];
    if (uniqueRates.length === 1) {
      effectiveTaxRate = uniqueRates[0];
    } else if (uniqueRates.length > 1) {
      effectiveTaxRate = null;
    } else {
      effectiveTaxRate = fallbackTaxRate;
    }
  }

  const showTax = settings.displayMode !== 'hidden';

  return {
    showTax,
    isTaxIncluded,
    subtotalHT,
    taxAmount: totalTaxAmount,
    taxRate: effectiveTaxRate,
    totalTTC,
    itemDetails,
  };
};

export const formatTaxForTicket = (result: TicketTaxResult): string => {
  const settings = getTicketTaxSettings();
  const lines: string[] = [];

  if (settings.displayMode === 'hidden') {
    return '';
  }

  if (settings.showItemTaxDetail && result.itemDetails.length > 0) {
    lines.push('[C]--------------------------------');
    lines.push('[C]DETAIL TVA PAR ARTICLE:');

    for (const item of result.itemDetails) {
      const taxRateFormatted = item.taxRate % 1 === 0 ? item.taxRate.toFixed(0) : item.taxRate.toFixed(1);
      const taxStr = `TVA ${taxRateFormatted}%: ${item.taxAmount.toFixed(2)}`;
      lines.push(`[L]  ${item.name} x${item.quantity}  [R]${taxStr}`);
    }
    lines.push('[C]--------------------------------');
  }

  if (settings.displayMode === 'detailed') {
    lines.push(`[L]Sous-total HT:    [R]${result.subtotalHT.toFixed(2)}`);

    if (result.taxRate !== null) {
      const taxRateFormatted = result.taxRate % 1 === 0 ? result.taxRate.toFixed(0) : result.taxRate.toFixed(1);
      lines.push(`[L]TVA (${taxRateFormatted}%):     [R]${result.taxAmount.toFixed(2)}`);
    } else {
      lines.push(`[L]TVA totale:       [R]${result.taxAmount.toFixed(2)}`);
    }

    lines.push(`[L]TOTAL TTC:        [R]${result.totalTTC.toFixed(2)}`);
  } else if (settings.displayMode === 'simple') {
    if (result.taxRate !== null) {
      const taxRateFormatted = result.taxRate % 1 === 0 ? result.taxRate.toFixed(0) : result.taxRate.toFixed(1);
      lines.push(`[L]TVA (${taxRateFormatted}%):     [R]${result.taxAmount.toFixed(2)}`);
    } else {
      lines.push(`[L]TVA:              [R]${result.taxAmount.toFixed(2)}`);
    }
    lines.push(`[L]TOTAL:            [R]${result.totalTTC.toFixed(2)}`);
  }

  return lines.join('\n');
};
